import React, { useState, useEffect } from "react";
import { X, AlertCircle } from "lucide-react";
import WhatsAppNewPreview from "./WhatsAppNewPreview";

const ViewEditTemplateModal = ({ isOpen, onClose, template }) => {
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    if (template) {
      // Transform template data to match WhatsAppNewPreview expected format
      const templateJson = template.template_json || {};
      const components = templateJson.components || [];
      
      // Extract body from BODY component
      const bodyComponent = components.find(c => c.type === 'BODY');
      const body = bodyComponent?.text || template.body || '';
      
      // Extract footer from FOOTER component
      const footerComponent = components.find(c => c.type === 'FOOTER');
      const footer = footerComponent?.text || template.footer || '';
      
      // Extract header from HEADER component
      const headerComponent = components.find(c => c.type === 'HEADER');
      const headerFormat = headerComponent?.format || template.header_type || template.headerFormat || 'NONE';
      const headerText = headerComponent?.text || template.headerText || '';
      const headerExample = headerComponent?.example || {};
      const headerMediaUrl =  template.header_media_url || null;

      // Extract buttons from BUTTONS component
      const buttonsComponent = components.find(c => c.type === 'BUTTONS');
      const buttons = buttonsComponent?.buttons || template.buttons || [];
      
      // Extract variables from body text
      const variableMatches = body.match(/\{\{(\d+)\}\}/g) || [];
      const variables = variableMatches.map((match, index) => {
        const varNum = match.replace(/\{\{|\}\}/g, '');
        const example = bodyComponent?.example?.body_text?.[index] || 
                       template.variables?.[index]?.sample || 
                       `Sample ${varNum}`;
        return {
          id: index + 1,
          name: varNum,
          sample: example
        };
      });
      
      // Determine template type
      let templateType = 'text';
      if (buttons.length > 0) {
        templateType = 'buttons';
      } else if (headerFormat && headerFormat !== 'NONE' && headerFormat !== 'TEXT') {
        templateType = headerFormat.toLowerCase();
      }
      
      // Ensure headerFormat is always a string
      const finalHeaderFormat = String(headerFormat || 'NONE');
      const finalHeaderType = finalHeaderFormat.toLowerCase();
      
      setPreviewData({
        name: template.name || '',
        category: template.category || 'marketing',
        language: template.language || 'en',
        templateType: template.template_type || templateType,
        headerFormat: finalHeaderFormat,
        headerType: finalHeaderType,
        headerText: headerText,
        header_media_url: headerMediaUrl,
        body: body,
        footer: footer,
        buttons: buttons.map((btn, idx) => ({
          id: idx + 1,
          type: btn.type,
          text: btn.text,
          url: btn.url,
          phone: btn.phone_number
        })),
        variables: variables
      });
    }
  }, [template, isOpen]);

  if (!isOpen || !template) return null;

  const templateJson = template.template_json || {};
  const components = templateJson.components || [];
  const bodyComponent = components.find(c => c.type === 'BODY');
  const footerComponent = components.find(c => c.type === 'FOOTER');
  const headerComponent = components.find(c => c.type === 'HEADER');
  const buttonsComponent = components.find(c => c.type === 'BUTTONS');

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
            <div>
              <h3 className="text-base font-bold text-gray-900">
                Template Preview
              </h3>
              <p className="text-xs text-gray-600 mt-0.5">
                Template: {template.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X size={16} className="text-gray-600" />
            </button>
          </div>

          <div
            className="flex-1 overflow-y-auto p-3"
            style={{ maxHeight: "calc(90vh - 120px)" }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {/* LEFT: Template Details */}
              <div className="lg:col-span-2 space-y-3">
                {/* Basic Info */}
                <div className="bg-white rounded-lg border border-gray-200 p-3">
                  <h4 className="text-xs font-bold text-gray-900 mb-2">
                    Template Information
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-700 mb-1">
                        Template Name
                      </label>
                      <p className="text-xs text-gray-900 bg-gray-50 px-2 py-1.5 rounded-lg">
                        {template.name}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-700 mb-1">
                          Category
                        </label>
                        <p className="text-xs text-gray-900 bg-gray-50 px-2 py-1.5 rounded-lg capitalize">
                          {template.category}
                        </p>
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-700 mb-1">
                          Language
                        </label>
                        <p className="text-xs text-gray-900 bg-gray-50 px-2 py-1.5 rounded-lg">
                          {template.language}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-700 mb-1">
                        Status
                      </label>
                      <p className="text-xs text-gray-900 bg-gray-50 px-2 py-1.5 rounded-lg capitalize">
                        {template.status}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Header */}
                {headerComponent && (
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <h4 className="text-xs font-bold text-gray-900 mb-2">
                      Header
                    </h4>
                    <div className="space-y-1.5">
                      <p className="text-xs text-gray-600">
                        <span className="font-semibold">Type:</span> {headerComponent.format || 'NONE'}
                      </p>
                      {headerComponent.text && (
                        <p className="text-xs text-gray-900 bg-gray-50 px-2 py-1.5 rounded-lg">
                          {headerComponent.text}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Body */}
                {bodyComponent && (
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <h4 className="text-xs font-bold text-gray-900 mb-2">
                      Message Body
                    </h4>
                    <div className="space-y-2">
                      {previewData?.variables && previewData.variables.length > 0 && (
                        <div>
                          <p className="text-[10px] font-semibold text-gray-700 mb-1.5">
                            Variables:
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {previewData.variables.map((variable) => (
                              <div
                                key={variable.id}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-[#25D366]/10 text-[#25D366] rounded-lg text-xs font-medium border border-[#25D366]/20"
                              >
                                <span>{`{{${variable.name}}}`}</span>
                                <span className="text-[10px] text-gray-600">
                                  ({variable.sample})
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-gray-900 bg-gray-50 px-2 py-2 rounded-lg whitespace-pre-wrap">
                          {bodyComponent.text}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Footer */}
                {footerComponent && footerComponent.text && (
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <h4 className="text-xs font-bold text-gray-900 mb-2">
                      Footer
                    </h4>
                    <p className="text-xs text-gray-900 bg-gray-50 px-2 py-1.5 rounded-lg">
                      {footerComponent.text}
                    </p>
                  </div>
                )}

                {/* Buttons */}
                {buttonsComponent && buttonsComponent.buttons && buttonsComponent.buttons.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <h4 className="text-xs font-bold text-gray-900 mb-2">
                      Buttons ({buttonsComponent.buttons.length})
                    </h4>
                    <div className="space-y-2">
                      {buttonsComponent.buttons.map((button, index) => (
                        <div
                          key={index}
                          className="p-2 border border-gray-200 rounded-lg space-y-1.5"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-700">
                              Button {index + 1} - {button.type.replace("_", " ")}
                            </span>
                          </div>
                          <p className="text-xs text-gray-900 bg-gray-50 px-2 py-1.5 rounded-lg">
                            {button.text}
                          </p>
                          {button.type === "URL" && button.url && (
                            <p className="text-[10px] text-gray-600">
                              URL: <a href={button.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{button.url}</a>
                            </p>
                          )}
                          {button.type === "PHONE_NUMBER" && button.phone_number && (
                            <p className="text-[10px] text-gray-600">
                              Phone: {button.phone_number}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rejection Reason */}
                {template.status === "rejected" && template.rejected_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={14} />
                      <div>
                        <p className="text-xs font-semibold text-red-900 mb-0.5">
                          Rejection Reason
                        </p>
                        <p className="text-xs text-red-700">
                          {template.rejected_reason}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT: WhatsApp Live Preview */}
              <div className="lg:col-span-1">
                <div className="sticky top-0">
                  {previewData && (
                    <WhatsAppNewPreview templateData={previewData} />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer Close Action */}
          <div className="flex items-center justify-end gap-2 p-2 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-xs border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewEditTemplateModal;
